
import React, { useState, useEffect } from 'react'
import Moment from 'moment';
import { Helmet } from 'react-helmet';
import { Route, Routes, Outlet, useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { useFormik, FormikProps } from 'formik'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import * as a from '../../../_ancientLibrary/components/'
import {useAncientRequest, useMessageModalContext, MODAL_TYPES, HttpMethod} from '../../../_ancientLibrary/utilities'

import {useAuth} from "../../../modules/auth";
import {AncientInputCheckbox, AncientTypeAhead} from "../../../_ancientLibrary/components/";
import {ID} from "../../../../_metronic/helpers";
import {CollectionListModel} from "../../_models/collectionModels";
import {UserCompanyCollection} from "../../../_apiModels/_authenticationModels";
import {TypeAheadCollection} from "../../../_apiModels/_libraryModels";

const usersBreadcrumbs: Array<PageLink> = [
    {
        title: 'Reports',
        path: '',
        isSeparator: false,
        isActive: true,
    },
]


type Props = {
    className: string
}
const API_URL = process.env.REACT_APP_API_URL

const editCollectionSchema = Yup.object().shape({})


const TypeAheadItem = ({item, onRemoveItem}: {
    item: TypeAheadCollection
    onRemoveItem: (id: ID) => void
}) => (
    <li key={item.id} className="d-flex justify-content-between align-items-center mb-2">
        <span>{item.label}</span>
        <span onClick={() => onRemoveItem(item.id)} className='btn btn-active-danger'>
          Remove
        </span>
    </li>
)

const ReportingSelection = () => {
    
    const { getSettingById, currentInstanceSettings,currentUser, userVerifyRoles, logout, auth } = useAuth()
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [companies, setCompanies] = useState<any[]>([]);
    const [reports, setReports] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [startPeriods, setStartPeriods] = useState<any[]>([]);
    const [endPeriods, setEndPeriods] = useState<any[]>([]);
    const [annualPeriods, setAnnualPeriods] = useState<any[]>([]);
    const [filteredEndPeriods, setFilteredEndPeriods] = useState<any[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<any>();
    const [selectedReport, setSelectedReport] = useState<any>();
    const [selectedGroup, setSelectedGroup] = useState<any>();
    const [selectedStartPeriod, setSelectedStartPeriod] = useState<any>();
    const [selectedEndPeriod, setSelectedEndPeriod] = useState<any>();
    const [selectedCalculationA, setSelectedCalculationA] = useState<any>();
    const [selectedCalculationB, setSelectedCalculationB] = useState<any>();
    const [calculations, setCalculations] = useState<any[]>([]);
    const [companySelected, setCompanySelected] = useState<boolean>(false);
    const [isDownloading, setDownloading] = useState<boolean>(false);
    const [showCustomReports, setShowCustomReports] = useState<boolean>(false);

    const isAdmin = userVerifyRoles([])
    
    const ancientRequest = useAncientRequest();

    var settingsReportTypes = currentInstanceSettings ? currentInstanceSettings["reportTypes" as keyof typeof currentInstanceSettings] : []
    var settingsPeriodTypes = currentInstanceSettings ? currentInstanceSettings["periodTypes" as keyof typeof currentInstanceSettings] : []
    var settingsPeriodTypeReporting = currentInstanceSettings ? currentInstanceSettings["periodTypeReporting" as keyof typeof currentInstanceSettings] : []


    const initialValues = {
        periodTypes: searchParams.get('periodTypes') || '',
    };

    const formik = useFormik({
        initialValues: { fileType: '6aba8918-711c-40e0-ab9d-bcbdddd5e29d', reportAllQuarters: false, reportList: []},
        validateOnChange: false,
        validationSchema: editCollectionSchema,
        enableReinitialize: true,
        onSubmit: async (values: any, {  }) => {
            setDownloading(true);
            // create an object like ReportSelection and pass it to /Reporting/DownloadReport
            
            let reportSelection = {
                companyId: values.companyId,
                reportName: showCustomReports && values.reportList?.length > 0
                    ? values.reportList[0].id.toString()  // Use the first custom report's ID
                    : values.reportId,         // Otherwise use the selected report ID
                groupId: values.groupId,
                startPeriod: values.startPeriod,
                endPeriod: values.endPeriod,
                reportType: values.reportType,
                reportAllQuarters: values.reportAllQuarters,
                fileType: values.fileType,
                periodTypeId: initialValues.periodTypes,
                calculationIdA: values.calculationIdA,
                calculationIdB: values.calculationIdB,
                hideLeaders: values.hideLeaders,
                hideAverage: values.hideAverage,
                hideLaggards: values.hideLaggards
            }
            await ancientRequest(HttpMethod.POST, `/bm/Reporting/DownloadReport`, reportSelection, undefined, {responseType: 'blob'}).then((response) => {
                if (response) {
                    const fileType = getSettingById('reportFileTypes', formik.values.fileType).extension;
                    // Create a blob from the response data
                    const blob = new Blob([response.data]);

                    // Create a URL for the blob
                    const blobUrl = URL.createObjectURL(blob);

                    // Create an anchor element for downloading the PDF
                    const downloadLink = document.createElement('a');
                    downloadLink.href = blobUrl;
                    downloadLink.download = `report.${fileType}`; // Set the desired filename
                    downloadLink.style.display = 'none';

                    // Trigger a click event on the download link to start the download
                    document.body.appendChild(downloadLink);
                    downloadLink.click();

                    // Clean up by revoking the blob URL
                    URL.revokeObjectURL(blobUrl);
                    setDownloading(false);
                }
            })
            .catch(error => {
                setDownloading(false);
            });
        },
    })

    React.useEffect(() => {
        loadCompanies();
    }, [initialValues.periodTypes])
    
    const loadCompanies = async () => {
        var companies = await ancientRequest(HttpMethod.GET, `/bm/Reporting/GetCompaniesByUserAccount/${initialValues.periodTypes}`);
        if (companies) {
            setCompanies(companies.data)
            setReports([]);
            setStartPeriods([]);
            setFilteredEndPeriods([]);
            setAnnualPeriods([]);
            setGroups([]);
        }
    }
    
    const loadReports = async (companyId : string) => {
        var reports = await ancientRequest(HttpMethod.GET, `/bm/Reporting/GetReportsByCompanyId/${companyId}/${initialValues.periodTypes}`);
        if (reports) {
            setReports(reports.data)
        }
    }

    const loadPeriods = async (companyId : string) => {
        var periodRanges = await ancientRequest(HttpMethod.GET, `/bm/Reporting/GetPeriodRangeByCompanyId/${companyId}/${initialValues.periodTypes}`);
        if (periodRanges) {
            setStartPeriods(periodRanges.data.startDates)
            setEndPeriods(periodRanges.data.endDates)
            setAnnualPeriods(periodRanges.data.annualDates)
        }
    }
    
    const loadGroups = async (companyId : string) => {
        var groups = await ancientRequest(HttpMethod.GET, `/bm/Reporting/GetGroupsByCompanyId/${companyId}/${initialValues.periodTypes}`);
        if (groups) {
            setGroups(groups.data)
        }
    }

    const loadCalculations = async (companyId : string) => {
        var calculations = await ancientRequest(HttpMethod.GET, `/bm/Reporting/GetCalculationsByCompanyId/${companyId}/${initialValues.periodTypes}`);
        if (calculations) {
            setCalculations(calculations.data)
        }
    }
    
    const onCompanyDropdownChange = (companyId: any) => {
        loadPeriods(companyId);
        loadReports(companyId);
        loadGroups(companyId);
        loadCalculations(companyId);
        setCompanySelected(true); // Set the companySelected state to true
    }
    const onStartDateDropdownChange = (selectedStartPeriodId: any) => {
        // Find the selected start period object based on its id
        const selectedStartPeriod = startPeriods.find((period) => period.id == selectedStartPeriodId);

        if (selectedStartPeriod) {
            // Convert the selected start date to a Date object
            const selectedStartDate = Moment(selectedStartPeriod.description, 'MM/DD/YYYY').toDate();

            // Update the state with the filtered end periods
            const updatedEndPeriods = endPeriods.map((endPeriod) => {
                const endDate = Moment(endPeriod.description, 'MM/DD/YYYY').toDate();

                // Compare the selected start date with the end date
                // Set the 'disabled' property based on the comparison result
                endPeriod.isDisabled = endDate < selectedStartDate;

                return endPeriod;
            });

            // Update the state with the updated end periods
            setFilteredEndPeriods(updatedEndPeriods);
        }
    }

    const checkIfAllFieldsSelected = () => {
        if (formik.values.companyId && formik.values.reportId && formik.values.startPeriod && formik.values.endPeriod && formik.values.groupId) {
            return true;
        }
        return false;
    }
    
    const getPeriodTypeByName = (settingName: string) => {
        var periodType = settingsPeriodTypeReporting?.find((periodType) => periodType.id === initialValues.periodTypes);
        return periodType?.settingName === settingName;
    }
    const PeriodDropdowns = () => {
    
        var periodType = settingsPeriodTypeReporting?.find((periodType) => periodType.id === initialValues.periodTypes);
        
        if (periodType?.settingName === "Labor") {
            return (
                <div className="row">
                    <div className="col-lg-12">
                        <a.AncientDropdown
                            id={"startPeriod"}
                            fieldProps={formik.getFieldProps(`startPeriod`)}
                            dropdownData={annualPeriods}
                            verticalLabel={true}
                            selectedValue={selectedStartPeriod}
                            optionKey="id"
                            optionLabel="description"
                            displayText="Year"
                            disabled={!companySelected || annualPeriods.length === 0}
                        />
                    </div>
                </div>
            )
        }
        else if (periodType?.settingName === "Benchmarking" || periodType?.settingName === "Benchmarking Graphs") {
            return (
                <div className="row">
                    <div className="col-lg-6">
                        <a.AncientDropdown
                            id={"startPeriod"}
                            fieldProps={formik.getFieldProps(`startPeriod`)}
                            dropdownData={startPeriods}
                            verticalLabel={true}
                            selectedValue={selectedStartPeriod}
                            optionKey="id"
                            optionLabel="description"
                            displayText="Start Date"
                            onChange={onStartDateDropdownChange}
                            disabled={!companySelected || startPeriods.length === 0}
                        />
                    </div>
                    <div className="col-lg-6">
                        <a.AncientDropdown
                            id={"endPeriod"}
                            fieldProps={formik.getFieldProps(`endPeriod`)}
                            dropdownData={filteredEndPeriods}
                            verticalLabel={true}
                            selectedValue={selectedEndPeriod}
                            optionKey="id"
                            optionLabel="description"
                            optionDisabled="isDisabled"
                            displayText="End Date"
                            disabled={!companySelected || filteredEndPeriods.length === 0}
                        />
                    </div>
                </div>
            )
        }
        
        return <></>
    }

    const removeItem = (id: any) => {
        const itemList = formik.values.reportList as TypeAheadCollection[]
        const newList = itemList.filter((item: TypeAheadCollection) => item.id !== id)
        formik.setFieldValue('reportList', newList)
    }

    const ReportSelection = () => {
        return (
            <>

                <Helmet>
                    <title>{getSettingById('periodTypeReporting', initialValues.periodTypes).settingDescription} Reports</title>
                </Helmet>
                <PageTitle breadcrumbs={usersBreadcrumbs}>Reports</PageTitle>
                {/*<form id='comparison' className='form' onSubmit={collectionDetailFormik.handleSubmit} noValidate>*/}
                    <a.AncientCard>
                        <a.AncientCardHeader>
                            <span>{getSettingById('periodTypeReporting', initialValues.periodTypes).settingDescription + " Reports"}</span>
                            <div></div>
                        </a.AncientCardHeader>
                        <a.AncientCardBody>

                            <div className="row">
                                <div className="col-lg-3">
                                </div>
                                <div className="col-lg-6">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <a.AncientDropdown
                                                id={"companyId"}
                                                fieldProps={formik.getFieldProps(`companyId`)}
                                                dropdownData={companies}
                                                verticalLabel={true}
                                                optionKey="id"
                                                optionLabel="companyName"
                                                optionGroup={isAdmin ? "reportGroup" : undefined}
                                                optionGroupSort={isAdmin ? "reportGroupSort" : undefined}
                                                displayText="Company"
                                                selectedValue={selectedCompany}
                                                onChange={onCompanyDropdownChange}
                                                disabled={companies.length === 0}
                                            />
                                        </div>
                                    </div>

                                    {PeriodDropdowns()}
                                    
                                    <div className="row">
                                        <div className="col-lg-12">
                                            {isAdmin && getPeriodTypeByName("Benchmarking") && (
                                                <a.AncientInputCheckbox
                                                    id="customReports"
                                                    displayText="Custom Reports"
                                                    fieldProps={{
                                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setShowCustomReports(e.target.checked);
                                                            if (!e.target.checked) {
                                                                formik.setFieldValue('reportList', []);
                                                            }
                                                        },
                                                        value: showCustomReports,
                                                        checked: showCustomReports
                                                    }}
                                                    touched={false}
                                                    errors={undefined}

                                                />
                                            )}

                                            {!showCustomReports && (
                                                <a.AncientDropdown
                                                    id={"reportId"}
                                                    fieldProps={formik.getFieldProps(`reportId`)}
                                                    dropdownData={reports}
                                                    verticalLabel={true}
                                                    selectedValue={selectedReport}
                                                    optionKey="selectionId"
                                                    optionLabel="settingDescription"
                                                    optionGroup="reportGroup"
                                                    optionGroupSort="reportGroupSort"
                                                    displayText="Report"
                                                    disabled={!companySelected || reports.length === 0}
                                                />
                                            )}

                                            {isAdmin && showCustomReports && (
                                                <>
                                                    <AncientTypeAhead
                                                        displayText='Search for a custom report'
                                                        formik={formik}
                                                        field='reportList'
                                                        apiEndpoint='/bm/reporting/typeAheadCustomReports/'
                                                        singleSelect={true}
                                                    />
                                                    {formik.values.reportList && formik.values.reportList.length > 0 && (
                                                        <div className="mb-3">
                                                            <h6>Selected Custom Reports:</h6>
                                                            <ul className="list-unstyled">
                                                                {formik.values.reportList.map((report: any, index: number) => (
                                                                    <TypeAheadItem
                                                                        item={report}
                                                                        onRemoveItem={removeItem}
                                                                    />
                                                                ))}
                                                            </ul>
                                                            <hr/>
                                                        </div>
                                                    )}

                                                </>
                                            )}

                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <a.AncientDropdown
                                                id={"groupId"}
                                                fieldProps={formik.getFieldProps(`groupId`)}
                                                dropdownData={groups}
                                                verticalLabel={true}
                                                selectedValue={selectedGroup}
                                                optionKey="settingName"
                                                optionLabel="settingDescription"
                                                displayText="Group"
                                                disabled={!companySelected || groups.length === 0}
                                            />
                                        </div>
                                    </div>

                                    {(getPeriodTypeByName("Benchmarking") || getPeriodTypeByName("Benchmarking Graphs")) &&
                                        <>
                                            <div className="row">
                                                <div className="col-lg-12">
                                                    <a.AncientDropdown
                                                        id={"reportType"}
                                                        fieldProps={formik.getFieldProps(`reportType`)}
                                                        dropdownData={settingsReportTypes}
                                                        verticalLabel={true}
                                                        selectedValue={selectedReport}
                                                        optionKey="id"
                                                        optionLabel="settingDescription"
                                                        displayText="Type"
                                                        disabled={!companySelected}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    }
                                    {getPeriodTypeByName("Benchmarking") &&
                                            <div className="row">
                                                <div className="col-lg-12">
                                                    <a.AncientInputCheckbox
                                                        id="allQuarters"
                                                        displayText={"Only return companies who reported all quarters."}
                                                        fieldProps={formik.getFieldProps('reportAllQuarters')}
                                                        touched={formik.touched.reportAllQuarters}
                                                        errors={formik.errors.reportAllQuarters}
                                                    />
                                                </div>
                                            </div>
                                    }

                                    {getPeriodTypeByName("Benchmarking Graphs") &&
                                        <>
                                            <div className="row">
                                                <div className="col-lg-12">
                                                    <a.AncientDropdown
                                                        id={"calculationIdA"}
                                                        fieldProps={formik.getFieldProps(`calculationIdA`)}
                                                        dropdownData={calculations}
                                                        verticalLabel={true}
                                                        selectedValue={selectedCalculationA}
                                                        optionKey="settingName"
                                                        optionLabel="settingDescription"
                                                        optionGroup="reportGroup"
                                                        optionGroupSort="reportGroupSort"
                                                        displayText="Calculation A"
                                                        disabled={!companySelected || groups.length === 0}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-lg-12">
                                                    <a.AncientDropdown
                                                        id={"calculationIdB"}
                                                        fieldProps={formik.getFieldProps(`calculationIdB`)}
                                                        dropdownData={calculations}
                                                        verticalLabel={true}
                                                        selectedValue={selectedCalculationB}
                                                        optionKey="settingName"
                                                        optionLabel="settingDescription"
                                                        optionGroup="reportGroup"
                                                        optionGroupSort="reportGroupSort"
                                                        displayText="Calculation A"
                                                        disabled={!companySelected || groups.length === 0}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="row">
                                                <div className="col-lg-4">
                                                    <a.AncientInputCheckbox
                                                        id="hideLeaders"
                                                        displayText={"Hide Leaders"}
                                                        fieldProps={formik.getFieldProps('hideLeaders')}
                                                        touched={formik.touched.hideLeaders}
                                                        errors={formik.errors.hideLeaders}
                                                    />
                                                </div>
                                                <div className="col-lg-4">
                                                    <a.AncientInputCheckbox
                                                        id="hideAverage"
                                                        displayText={"Hide Average"}
                                                        fieldProps={formik.getFieldProps('hideAverage')}
                                                        touched={formik.touched.hideAverage}
                                                        errors={formik.errors.hideAverage}
                                                    />
                                                </div>
                                                <div className="col-lg-4">
                                                    <a.AncientInputCheckbox
                                                        id="hideLaggards"
                                                        displayText={"Hide Laggards"}
                                                        fieldProps={formik.getFieldProps('hideLaggards')}
                                                        touched={formik.touched.hideLaggards}
                                                        errors={formik.errors.hideLaggards}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    }
                                    
                                    
                                    
                                    
                                    <div className={"row"}>
                                        <div className="col-lg-8">
                                            <a.AncientButton icon={"/media/icons/duotune/general/gen016.svg"}
                                                             isFormSubmit={true}
                                                             displayText={"Download Report"}
                                                             disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                                                             onClick={formik.handleSubmit}
                                                             margin={"mx-0"}
                                                             submitting={isDownloading}
                                                             submittingMessage={"Generating report..."}
                                            />
                                        </div>
                                        <div className="col-lg-4 ">
                                            <a.AncientDropdownInstanceSetting
                                                dropdownData="reportFileTypes"
                                                className={"float-end"}
                                                fieldProps={formik.getFieldProps(`fileType`)}
                                                verticalLabel={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3">
                                </div>  
                            </div>
                        </a.AncientCardBody>
                    </a.AncientCard>
                {/*</form>*/}
            </>
        )
    }

    return (
        <Routes>
            <Route element={<Outlet />}>
                <Route
                    path='*'
                    element={ReportSelection()}
                />
            </Route>
        </Routes>
    )
}

export default ReportingSelection
